// Account settings card: the host's own venues and how long questions stay off-limits at a venue.
import React, { useState } from "react";
import { addVenue, hideVenue, setRepeatMonths, useHostVenues, REPEAT_MONTH_OPTIONS, repeatLabel, TEST_VENUE } from "./venues";
import { appAlert } from "./components/AppDialog";

const box = {
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 14,
  padding: "12px 14px",
  marginBottom: 10,
};
const label = { fontWeight: 800, color: "#d6e6ff", fontSize: "0.95rem" };
const btn = {
  padding: "8px 14px",
  background: "linear-gradient(135deg, rgba(255,204,102,0.90), rgba(255,150,64,0.95))",
  color: "#1d2030",
  fontWeight: 900,
  borderRadius: 12,
  border: "none",
  cursor: "pointer",
};
const input = {
  flex: 1,
  minWidth: 0,
  padding: "8px 12px",
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,0.25)",
  background: "rgba(0,0,0,0.3)",
  color: "#fff",
  fontSize: "1rem",
};

export default function VenueSettings() {
  const { uid, loaded, venues, hidden, repeatMonths } = useHostVenues();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { appAlert(`Couldn't save: ${e?.message || e}`); }
    finally { setBusy(false); }
  };

  const onAdd = () => {
    const v = name.trim();
    if (!v) return;
    if (v === TEST_VENUE) { appAlert(`"${TEST_VENUE}" is always available for rehearsals.`); return; }
    run(async () => { await addVenue(uid, v, hidden); setName(""); });
  };

  return (
    <div style={box}>
      <div style={label}>Your venues</div>
      <div style={{ opacity: 0.8, fontSize: "0.9rem", margin: "4px 0 10px" }}>
        Pick one of these when you host. "{TEST_VENUE}" is always there for rehearsals.
      </div>

      {!loaded ? (
        <div style={{ opacity: 0.7 }}>Loading…</div>
      ) : venues.length === 0 ? (
        <div style={{ opacity: 0.7, marginBottom: 8 }}>No venues yet. Add the pubs and clubs where you host.</div>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
          {venues.map((v) => (
            <span key={v} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 10px",
              borderRadius: 999, background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.18)" }}>
              {v}
              <button
                title={`Remove ${v} from your list (question history is kept)`}
                disabled={busy}
                onClick={() => run(() => hideVenue(uid, v))}
                style={{ background: "none", border: "none", color: "#ff9a9a", cursor: "pointer", fontWeight: 900, padding: 0 }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <input
          style={input}
          placeholder="Add a venue, e.g. The Rose and Crown"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") onAdd(); }}
          maxLength={60}
        />
        <button style={{ ...btn, opacity: busy || !name.trim() ? 0.6 : 1 }} disabled={busy || !name.trim()} onClick={onAdd}>
          Add
        </button>
      </div>

      <div style={{ ...label, marginTop: 16 }}>Don't repeat questions at the same venue</div>
      <div style={{ opacity: 0.8, fontSize: "0.9rem", margin: "4px 0 8px" }}>
        Library questions already played at a venue are skipped there for this long.
      </div>
      <select
        value={repeatMonths}
        disabled={busy || !loaded}
        onChange={(e) => run(() => setRepeatMonths(uid, Number(e.target.value)))}
        style={{ ...input, flex: "none", width: "100%" }}
      >
        {REPEAT_MONTH_OPTIONS.map((m) => (
          <option key={m} value={m}>{repeatLabel(m)}</option>
        ))}
      </select>
    </div>
  );
}
